import theme from '../theme/createKuramaTheme'
import { ComparisonDecrease } from './assets/ComparisonDecrease'
import { ComparisonIncrease } from './assets/ComparisonIncrease'
import { ComparisonNeutral } from './assets/ComparisonNeutral'
import { Diamond } from './assets/Diamond'
import { LineDashed } from './assets/LineDashed'
import { LineDotted } from './assets/LineDotted'
import { LineSolid } from './assets/LineSolid'
import { Rectangle } from './assets/Rectangle'
import { RevertedTriangle } from './assets/RevertedTriangle'
import { Star } from './assets/Star'
import { Triangle } from './assets/Triangle'
import type { ThemeColor } from '../theme/palette'
import type { Theme } from '../theme/themeTypes'
import { getNamedColorConfig, migrateNamedColor } from '../theme/namedColors/resolve'
import type { NamedColor } from '../theme/namedColors/types'

export const borderedBadgeShapeNames = [
  'rectangle',
  'triangle',
  'diamond',
  'lineSolid',
  'lineDashed',
  'lineDotted',
  'revertedTriangle',
  'star'
] as const

export const comparisonBadgeShapeNames = ['comparisonNeutral', 'comparisonIncrease', 'comparisonDecrease'] as const

export const badgeShapeNames = [...borderedBadgeShapeNames, ...comparisonBadgeShapeNames] as const

export const DEFAULT_BADGE_SHAPE = 'rectangle'

export const badgeBorderVariants = ['darker', 'lighter'] as const

export const legacyBadgeColors = [
  'pink',
  'steel',
  'mint',
  'dark-mint',
  'beige',
  'dark-beige',
  'indigo',
  'dark-indigo',
  'yellow',
  'red',
  'dark-red',
  'green',
  'purple',
  'dark-purple',
  'extra-dark-purple',
  'success',
  'error',
  'warning',
  'info',
  'stone'
] as const

export const tokenBadgeColors = [
  'precis-blue',
  'cobalt',
  'teal',
  'magenta',
  'orange',
  'brown',
  'violet',
  'andesite',
  'graph-pink',
  'olive',
  'sienna',
  'graph-green',
  'caramel',
  'aqua',
  'salmon',
  'brass',
  'semantic-error',
  'semantic-warning',
  'semantic-success',
  'semantic-info',
  'neutral'
] as const

export const comparisonBadgeColors = ['comparison-positive', 'comparison-negative', 'comparison-neutral'] as const

export const badgeColors = [...legacyBadgeColors, ...tokenBadgeColors, ...comparisonBadgeColors] as const

type LegacyBadgeColor = (typeof legacyBadgeColors)[number]
type TokenBadgeColor = (typeof tokenBadgeColors)[number]
type BadgeColor = (typeof badgeColors)[number]
type BadgeBorderVariant = (typeof badgeBorderVariants)[number]

export interface BadgeColorConfig {
  backgroundColor: ThemeColor
  borderColor?: Record<BadgeBorderVariant, ThemeColor>
}

export const legacyBadgeColorMigrationMap = new Map<LegacyBadgeColor, TokenBadgeColor>([
  ['pink', 'graph-pink'],
  ['steel', 'neutral'],
  ['mint', 'graph-green'],
  ['dark-mint', 'teal'],
  ['beige', 'caramel'],
  ['dark-beige', 'caramel'],
  ['indigo', 'cobalt'],
  ['dark-indigo', 'violet'],
  ['yellow', 'brass'],
  ['red', 'salmon'],
  ['dark-red', 'orange'],
  ['green', 'graph-green'],
  ['purple', 'violet'],
  ['dark-purple', 'andesite'],
  ['extra-dark-purple', 'andesite'],
  ['success', 'semantic-success'],
  ['error', 'semantic-error'],
  ['warning', 'semantic-warning'],
  ['info', 'semantic-info'],
  ['stone', 'neutral']
])

export const migrateBadgeColor = (color: string): string => {
  const legacyMigratedColor = legacyBadgeColorMigrationMap.get(color as LegacyBadgeColor)
  if (legacyMigratedColor != null) {
    return legacyMigratedColor
  }

  return color
}

export const getBadgeColorConfig = (color: string, activeTheme: Theme): BadgeColorConfig | undefined => {
  try {
    return getNamedColorConfig(migrateNamedColor(color) as NamedColor, activeTheme)
  } catch {
    return undefined
  }
}

export const createColorMap = (activeTheme: Theme): Map<BadgeColor, ThemeColor> =>
  new Map(
    badgeColors.map(
      (badgeColor) =>
        [
          badgeColor,
          getBadgeColorConfig(badgeColor, activeTheme)?.backgroundColor ?? (badgeColor as ThemeColor)
        ] as const
    )
  )

/** @deprecated Use `createColorMap(theme)` — resolves against the default light theme only. */
export const colorMap = createColorMap(theme)

export const createBadgeColorMap = (activeTheme: Theme): Map<string, BadgeColorConfig> =>
  new Map(
    badgeColors.flatMap((badgeColor) => {
      const config = getBadgeColorConfig(badgeColor, activeTheme)
      return config != null ? ([[badgeColor, config]] as const) : []
    })
  )

/** @deprecated Use `createBadgeColorMap(theme)` — resolves against the default light theme only. */
export const badgeColorMap = createBadgeColorMap(theme)

export const badgeShapeMap = {
  rectangle: {
    component: Rectangle,
    hasBorder: true
  },
  triangle: {
    component: Triangle,
    hasBorder: true
  },
  diamond: {
    component: Diamond,
    hasBorder: true
  },
  lineSolid: {
    component: LineSolid,
    hasBorder: true
  },
  lineDashed: {
    component: LineDashed,
    hasBorder: true
  },
  lineDotted: {
    component: LineDotted,
    hasBorder: true
  },
  revertedTriangle: {
    component: RevertedTriangle,
    hasBorder: true
  },
  star: {
    component: Star,
    hasBorder: true
  },
  comparisonNeutral: {
    component: ComparisonNeutral,
    hasBorder: false
  },
  comparisonIncrease: {
    component: ComparisonIncrease,
    hasBorder: false
  },
  comparisonDecrease: {
    component: ComparisonDecrease,
    hasBorder: false
  }
} as const
